import { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import LogoMinymol from '../../components/LogoMinymol/LogoMinymol';
import Footer from "../../components/Footer/Footer";
import CodigoInput from "../../components/CodigoInput/CodigoInput";
import Alert from "../../components/Alert/Alert";
import './RecuperarContrasena.css';

export default function RecuperarContrasena() {
    const [step, setStep] = useState(1);
    const [numero, setNumero] = useState("");
    const [numeroInternacional, setNumeroInternacional] = useState("");
    const [codigo, setCodigo] = useState("");
    const [nuevaPass, setNuevaPass] = useState("");
    const [confirmarPass, setConfirmarPass] = useState("");

    const [showPass, setShowPass] = useState(false);
    const [showRepeat, setShowRepeat] = useState(false);
    const [alerta, setAlerta] = useState(null);

    useEffect(() => {
        const savedNumero = localStorage.getItem('recuperarNumero');
        const savedNumeroInt = localStorage.getItem('recuperarNumeroInt');
        const savedStep = parseInt(localStorage.getItem('recuperarPaso'), 10);
        if (savedNumero) setNumero(savedNumero);
        if (savedNumeroInt) setNumeroInternacional(savedNumeroInt);
        if (!isNaN(savedStep) && savedStep >= 1 && savedStep <= 2) {
            setStep(savedStep);
        }
    }, []);

    const avanzar = () => {
        const next = step + 1;
        setStep(next);
        // sólo persistir cuando vamos de paso 1 → 2
        if (step === 1) {
            localStorage.setItem('recuperarPaso', next.toString());
        }
    };

    const validarNumero = async (e) => {
        e.preventDefault();
        if (numero.length < 7 || numero.length > 12) {
            setAlerta({ type: 'error', message: 'Número inválido. Ej: 3001234567' });
            return;
        }

        try {
            const res = await axios.get(`https://api.minymol.com/users/resolve-phone/${numero}`);
            const fullPhone = res.data.phoneNumber;

            await axios.post(`https://api.minymol.com/notifications/verify/resend`, {
                channel: 'whatsapp',
                type: 'recover_password',
                phoneNumber: fullPhone,
            });

            setNumeroInternacional(fullPhone);
            localStorage.setItem('recuperarNumero', numero);
            localStorage.setItem('recuperarNumeroInt', fullPhone);
            avanzar();
        } catch (err) {
            setAlerta({ type: 'error', message: 'Número no registrado o error al enviar el código.' });
        }
    };

    const validarCodigo = async () => {
        try {
            await axios.post(`https://api.minymol.com/notifications/verify/confirm`, {
                channel: 'whatsapp',
                type: 'recover_password',
                phoneNumber: numeroInternacional,
                code: codigo,
            });
            localStorage.removeItem('recuperarNumero');
            localStorage.removeItem('recuperarNumeroInt');
            localStorage.removeItem('recuperarPaso');
            avanzar();
        } catch (err) {
            setAlerta({ type: 'error', message: 'El código ingresado es incorrecto o expiró.' });
        }
    };

    const cambiarContrasena = async () => {
        if (nuevaPass !== confirmarPass || nuevaPass.length < 10) {
            setAlerta({ type: 'error', message: 'Las contraseñas no coinciden o son muy cortas' });
            return;
        }

        try {
            await axios.patch(`https://api.minymol.com/users/recover-password`, {
                phoneNumber: numero,
                code: codigo,
                newPassword: nuevaPass,
            });

            setAlerta({
                type: 'success',
                message: 'Contraseña actualizada correctamente',
                redirectTo: '/login',
            });
        } catch (err) {
            setAlerta({ type: 'error', message: 'Error al cambiar la contraseña. Intenta de nuevo.' });
        }
    };

    const passwordValidation = {
        hasLetter: /[A-Za-z]/.test(nuevaPass),
        hasNumberOrSymbol: /[0-9\W]/.test(nuevaPass),
        hasMinLength: nuevaPass.length >= 10,
    };

    const clasePantalla = (s) =>
        s === step
            ? "pantalla-recuperacion pantalla-activa"
            : s < step
                ? "pantalla-recuperacion pantalla-anterior"
                : "pantalla-recuperacion pantalla-siguiente";

    const maskPhone = (num = '') => {
        const clean = num.replace(/\D/g, '');
        if (clean.length < 4) return '';
        return `+${clean.slice(0, -7).padStart(3, '*')} *** *** ${clean.slice(-3)}`;
    };

    return (
        <>
            <div className="recuperar-container">
                <LogoMinymol />

                <div className="pantallas-recuperacion">
                    {/* Paso 1 */}
                    <div className={clasePantalla(1)}>
                        <form onSubmit={validarNumero} className="recuperar-form">
                            <h2>Recuperar contraseña</h2>
                            <p>Ingresa el número de celular con el que creaste tu cuenta para recibir un código por WhatsApp.</p>
                            <input
                                type="tel"
                                placeholder="Ej: 3001234567"
                                value={numero}
                                onChange={(e) => setNumero(e.target.value)}
                            />
                            <button type="submit">Enviar código</button>
                        </form>
                    </div>

                    {/* Paso 2 */}
                    <div className={clasePantalla(2)}>
                        <div className="recuperar-form">
                            <h2>Verificar código</h2>
                            <p>
                                Ingresa el código enviado por WhatsApp al número <strong>{maskPhone(numeroInternacional)}</strong>
                            </p>
                            <CodigoInput longitud={6} onComplete={setCodigo} />
                            <button onClick={validarCodigo}>Verificar código</button>
                            <p>
                                ¿No recibiste el código?{" "}
                                <span onClick={() => setStep(1)}>Volver a enviar</span>
                            </p>
                        </div>
                    </div>

                    {/* Paso 3 */}
                    <div className={clasePantalla(3)}>
                        <div className="recuperar-form">
                            <h2>Restablecer contraseña</h2>

                            <label>Contraseña nueva</label>
                            <div className="contraseña-wrapper">
                                <input
                                    type={showPass ? "text" : "password"}
                                    value={nuevaPass}
                                    onChange={(e) => setNuevaPass(e.target.value)}
                                    placeholder="Ingrese la nueva contraseña"
                                />
                                <span className="toggle-pass" onClick={() => setShowPass(!showPass)}>
                                    <FontAwesomeIcon icon={showPass ? faEyeSlash : faEye} />
                                </span>
                            </div>

                            <label>Repetir contraseña</label>
                            <div className="contraseña-wrapper">
                                <input
                                    type={showRepeat ? "text" : "password"}
                                    value={confirmarPass}
                                    onChange={(e) => setConfirmarPass(e.target.value)}
                                    placeholder="Repita la contraseña"
                                />
                                <span className="toggle-pass" onClick={() => setShowRepeat(!showRepeat)}>
                                    <FontAwesomeIcon icon={showRepeat ? faEyeSlash : faEye} />
                                </span>
                            </div>

                            <div className="contraseña-validacion">
                                <p className={passwordValidation.hasLetter ? "valid" : "invalid"}>
                                    {passwordValidation.hasLetter ? "✔ " : ""} Al menos 1 letra
                                </p>
                                <p className={passwordValidation.hasNumberOrSymbol ? "valid" : "invalid"}>
                                    {passwordValidation.hasNumberOrSymbol ? "✔ " : ""} Número o carácter especial
                                </p>
                                <p className={passwordValidation.hasMinLength ? "valid" : "invalid"}>
                                    {passwordValidation.hasMinLength ? "✔ " : ""} Mínimo 10 caracteres
                                </p>
                            </div>

                            <button onClick={cambiarContrasena}>Cambiar contraseña</button>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {alerta && (
                <Alert
                    type={alerta.type}
                    message={alerta.message}
                    onClose={() => setAlerta(null)}
                    redirectTo={alerta.redirectTo}
                />
            )}
        </>
    );
}
